import { FormHelperText, styled } from '@mui/material';
import { FieldWrapper } from 'DesignSystem';
import { FastField as Field, useField } from 'formik';
import { TextField } from 'formik-mui';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';

export interface ITextInputFieldProps {
  disabled?: boolean;
  label: string;
  name: string;
  subtitle?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  helperText?: string;
  multiline?: boolean;
  isRequired?: boolean;
  /**
   * Maximum number of characters allowed in the input field.
   */
  maxlength?: number;
  /**
   * Maximum number of rows to display when multiline option is set to true.
   */
  maxRows?: string | number;
  /**
   * Minimum number of rows to display when multiline option is set to true.
   */
  minRows?: string | number;
}

export const CharacterLimit = styled(FormHelperText)<{ visible: Tbooleanish }>(({ visible }) => ({
  position: 'absolute',
  right: 0,
  bottom: -20,
  opacity: visible === 'true' ? 1 : 0,
  transform: visible === 'true' ? 'translateY(0)' : 'translateY(-20px)',
  transition: 'all 0.06s ease-out',
}));

const TextInputField: FC<ITextInputFieldProps> = ({
  disabled,
  label,
  subtitle,
  helperText,
  isRequired,
  size = 'medium',
  maxlength,
  ...props
}) => {
  const [{ value }, , { setValue }] = useField(props.name);
  const [focused, setFocused] = useState(false);

  // if text already exists and it is longer than the limit, then the limit is not applied
  const applyCharLimit = useMemo(() => {
    return maxlength && (value || '').length <= maxlength;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxlength]);

  // Workaround for clearing the field when the value is undefined
  useEffect(() => {
    if (value === undefined) {
      setValue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  return (
    <FieldWrapper
      label={label}
      subtitle={subtitle}
      variant={size}
      sx={{ position: 'relative' }}
      required={isRequired}
      tooltip={
        helperText
          ? {
              helperText,
              variant: 'INFO',
            }
          : undefined
      }
    >
      <Field
        disabled={disabled}
        component={TextField}
        inputProps={{
          maxlength,
          onBlur: handleBlur,
          onFocus: handleFocus,
        }}
        fullWidth
        size={size}
        variant="outlined"
        {...props}
      />
      {applyCharLimit && (
        <CharacterLimit visible={Booleanish(focused)}>{`${
          (value || '').length
        }/${maxlength}`}</CharacterLimit>
      )}
    </FieldWrapper>
  );
};

export default TextInputField;
